/**
 * The flows that can park an OAuth tab on one of okthx's own mid-flow pages
 * and send it back to a start route afterwards.
 *
 * Shared rather than declared on each side, because the value crosses the
 * boundary as a bare query parameter: the api writes it into the URL it parks
 * the tab on, and the client reads it back to decide which start route Continue
 * returns to. Those two routes do different things to the caller's session —
 * `login` signs the user in AS the account they pick, `add_account` adds it
 * under the user they already are — so a value the client fails to recognise
 * does not merely pick a different URL, it produces a different outcome. With
 * one list and a typed api parameter, a mistyped flow is a compile error
 * instead.
 *
 * `login` covers signing in and signing up: they are one route.
 *
 * ADDING A VALUE HERE IS A TWO-DEPLOY CHANGE. The list is shared at compile
 * time, but the running api and the browser's bundle are not: the api ships in
 * a container and the client from a CDN. An api that emits a flow the loaded
 * bundle predates hands that bundle a value it cannot resolve, and the screen
 * refuses to continue rather than guess — the right answer for the user, and a
 * dead end all the same. So ship the client that knows the new value first, and
 * only then let the api emit it.
 */
export const AUTH_FLOWS = ["login", "add_account"] as const;

export type AuthFlow = (typeof AUTH_FLOWS)[number];

/** Whether a query-string value names one of the known flows. */
export const isAuthFlow = (value: string | null | undefined): value is AuthFlow =>
  value !== null && value !== undefined && (AUTH_FLOWS as readonly string[]).includes(value);

/**
 * Which vocabulary the `access` ids on a start-route URL are written in.
 *
 * WHY A VERSION AND NOT JUST THE IDS. `contacts` used to mean one Google scope
 * and now means two, because the screen stopped asking about the work directory
 * separately. An `access` string alone cannot say which meaning was intended:
 * `email,calendar,contacts` is what a CURRENT client sends when the user
 * accepted everything, and also what a client shipped before the merge sends
 * when the user ticked Contacts and deliberately UNTICKED Work directory. Read
 * as the current vocabulary, that second user is handed the directory they just
 * refused.
 *
 * The web bundle updates on reload, so the window there is a stale tab. An iOS
 * build does not: it ships as a binary and lives on the device until the user
 * updates, so the pre-merge screen stays in the field for as long as that takes.
 *
 * A client that knows the merged meaning says so. Anything that does not is read
 * in the OLD vocabulary, where each id names exactly one scope — which is what
 * those clients put on screen, so it is what their user answered. The default
 * therefore narrows rather than widens, which is the only safe direction for a
 * value that decides what okthx may reach.
 *
 * Bump this only when an id's MEANING changes. Adding or removing an id does
 * not need it: an unknown id already contributes no scopes.
 */
export const ACCESS_VOCABULARY_VERSION = "2";

/** The query parameter that carries {@link ACCESS_VOCABULARY_VERSION}. */
export const ACCESS_VOCABULARY_PARAM = "access_v";

/** Stable client-facing provenance code for a rejected credential. */
export const AUTH_REJECTED_CODE = "auth_rejected";

export type AuthRejectedBody = {
  code: typeof AUTH_REJECTED_CODE;
  message: string;
};

export type SocketAuthErrorDataByKind = {
  rejected: { code: typeof AUTH_REJECTED_CODE; retryable: false };
  unavailable: { retryable: true };
  internal: { retryable: false };
};

export type SocketAuthErrorData = SocketAuthErrorDataByKind[keyof SocketAuthErrorDataByKind];

/**
 * The access ids the grant-required page names, per provider — one id per
 * provider scope, so the page can say WHICH half of a merged item is missing.
 *
 * NOT the same vocabulary as the selection screen's `PROVIDER_ACCESS_IDS`, and
 * the difference is the point. That screen offers choices, so it asks about
 * contacts once and spends two Google scopes on the answer. This page reports a
 * partial grant, and Google's granular consent lets a user refuse the directory
 * alone — told only that "Contacts" failed, a user who DID grant contacts has no
 * way to know which line to re-tick.
 *
 * Shared because three places have to agree on these strings and only one of
 * them can see the others: the api builds the `granted` ids from
 * `DIAGNOSABLE_GOOGLE_SCOPES` and `EXPLAINABLE_MICROSOFT_SCOPES`, and the web
 * page matches its rows against them. A rename on either side was silent — the
 * row simply never marked itself granted, and the user was told to re-grant
 * something they already had. The handler tests pin both tables to this list.
 */
export const DIAGNOSABLE_ACCESS_IDS: Record<string, readonly string[]> = {
  google: ["email", "calendar", "contacts", "directory"],
  microsoft: ["email", "calendar", "contacts"],
};
